import type {
  EditorState,
  EditableContentUnit,
  RawComponent,
  RawContentSection,
  RawQuestion,
  RawQuestionSection,
  RawContentUnit,
  RawSection,
  RawVideoSection,
  UpdateCourseSavePayload,
} from './editorTypes';
import { extractStandaloneEquationLatex } from './editorNormalizer';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function normalizeString(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
}

function normalizeIdentifier(value: unknown): string | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }

  if (typeof value === 'bigint') {
    return value.toString();
  }

  return normalizeString(value);
}

function toSectionArray(value: unknown): RawSection[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter(isRecord) as RawSection[];
}

function getContentSection(section: RawSection): RawContentSection | null {
  const contentSection = section.content_section ?? section.contentSection;
  return isRecord(contentSection) ? (contentSection as RawContentSection) : null;
}

function getVideoSection(section: RawSection): RawVideoSection | null {
  const videoSection = section.video_section ?? section.videoSection;
  return isRecord(videoSection) ? (videoSection as RawVideoSection) : null;
}

function getQuestionSection(section: RawSection, key: 'homework' | 'preLecture') {
  const questionSection =
    key === 'homework'
      ? section.question_section ?? section.questionSection
      : section.pre_lecture_question_section ?? section.preLectureQuestionSection;

  return isRecord(questionSection) ? (questionSection as RawQuestionSection) : null;
}

function cloneComponent<T>(value: T): T {
  if (typeof structuredClone === 'function') {
    return structuredClone(value);
  }

  return JSON.parse(JSON.stringify(value)) as T;
}

function toSnakeCaseKey(value: string) {
  return value.replace(/[A-Z]/g, (match) => `_${match.toLowerCase()}`);
}

function toSnakeCaseValue<T>(value: T): T {
  if (Array.isArray(value)) {
    return value.map((entry) => toSnakeCaseValue(entry)) as T;
  }

  if (!isRecord(value)) {
    return value;
  }

  return Object.entries(value).reduce<Record<string, unknown>>((result, [key, entry]) => {
    result[toSnakeCaseKey(key)] = toSnakeCaseValue(entry);
    return result;
  }, {}) as T;
}

function getSerializedTextValue(unit: EditableContentUnit) {
  if (unit.editorType === 'EQUATION') {
    const latex = unit.latex?.trim() ?? '';
    const originalLatex = extractStandaloneEquationLatex(unit.originalText);

    if (unit.originalText && originalLatex === latex) {
      return unit.originalText;
    }

    return `$$${latex}$$`;
  }

  if (unit.originalText !== undefined && unit.text === unit.originalText) {
    return unit.originalText;
  }

  return unit.text ?? unit.originalText ?? '<p></p>';
}

function serializeContentUnit(unit: EditableContentUnit): RawContentUnit {
  const baseUnit: RawContentUnit = {
    caption: normalizeString(unit.caption) ?? undefined,
    collapse_by_default: unit.collapseByDefault,
    disable_auto_numbering: unit.disableAutoNumbering,
    heading: normalizeString(unit.heading) ?? undefined,
    id: unit.id,
  };

  if (unit.editorType === 'IMAGE') {
    return {
      ...baseUnit,
      alt_text: normalizeString(unit.altText) ?? undefined,
      dynamic_image_url: normalizeString(unit.dynamicImageUrl) ?? undefined,
      image_size:
        unit.imageSize === 'SMALL' ? 1 : unit.imageSize === 'LARGE' ? 3 : 2,
      image_url: normalizeString(unit.imageUrl) ?? undefined,
      type: 2,
    };
  }

  if (unit.editorType === 'VIDEO') {
    return {
      ...baseUnit,
      type: 3,
      video_id: normalizeString(unit.videoId) ?? undefined,
    };
  }

  if (unit.editorType === 'IFRAME') {
    return {
      ...baseUnit,
      iframe_unit: {
        height: unit.iframeHeight ?? 420,
        source: normalizeString(unit.iframeSource) ?? '',
        width: unit.iframeWidth ?? 100,
      },
      type: 4,
    };
  }

  if (unit.editorType === 'TABLE') {
    return {
      ...baseUnit,
      table_unit: {
        density: unit.tableDensity === 'compact' ? 1 : 0,
        // Written unconditionally, including when false. The normalizer reads an
        // ABSENT header_row as true (old tables predate the field), so omitting
        // a false here would silently turn the header back on.
        header_row: unit.tableHeaderRow ?? true,
        html: unit.tableHtml ?? '',
        sticky_header: unit.tableStickyHeader ?? false,
        style:
          unit.tableStyle === 'striped' ? 1 : unit.tableStyle === 'bordered' ? 2 : 0,
      },
      type: 5,
    };
  }

  return {
    ...baseUnit,
    text: getSerializedTextValue(unit),
    type: 1,
  };
}

export function serializeEditorState(
  editorState: Pick<
    EditorState,
    | 'baseComponent'
    | 'contentUnits'
    | 'preLectureQuestionSectionId'
    | 'preLectureQuestionSectionOwnerSectionId'
    | 'preLectureQuestionSectionUid'
    | 'questionSectionId'
    | 'questionSectionOwnerSectionId'
    | 'questionSectionUid'
    | 'videoIds'
    | 'videoSectionId'
    | 'videoSectionOwnerSectionId'
    | 'videoSectionUid'
  >,
  courseId: string,
  finalizedQuestions?: {
    preLectureQuestions?: RawQuestion[];
    questions?: RawQuestion[];
  },
): UpdateCourseSavePayload {
  const component = toSnakeCaseValue(cloneComponent(editorState.baseComponent));
  const sections = toSectionArray(component.sections);

  if (sections.length === 0) {
    throw new Error('Cannot save content because the component has no sections.');
  }

  let hasContentSection = false;

  sections.forEach((section, sectionIndex) => {
    const sectionId =
      normalizeIdentifier(section.id) ??
      normalizeIdentifier(section.uid) ??
      `__generated-section-${sectionIndex + 1}`;
    const contentSection = getContentSection(section);

    const contentSectionId =
      normalizeIdentifier(contentSection?.id) ??
      normalizeIdentifier(contentSection?.uid) ??
      `__generated-content-section-${sectionIndex + 1}`;
    const contentUnits = editorState.contentUnits
      .filter((unit) => {
        if (unit.sourceSectionIndex !== sectionIndex || unit.sourceSectionId !== sectionId) {
          return false;
        }

        if (!contentSection) {
          return true;
        }

        return (
          unit.sourceContentSectionIndex === sectionIndex &&
          unit.sourceContentSectionId === contentSectionId
        );
      })
      .map((unit) => serializeContentUnit(unit));

    const shouldCreateEditableContentSection =
      !contentSection &&
      (
        contentUnits.length > 0 ||
        sectionId === editorState.videoSectionOwnerSectionId ||
        sectionId === editorState.questionSectionOwnerSectionId ||
        sectionId === editorState.preLectureQuestionSectionOwnerSectionId
      );

    if (!contentSection && !shouldCreateEditableContentSection) {
      return;
    }

    hasContentSection = true;

    const nextContentSection: RawContentSection = {
      ...(contentSection ?? {
        id: contentSectionId,
      }),
      content_units: contentUnits,
    };

    delete (nextContentSection as RawContentSection & { contentUnits?: unknown })
      .contentUnits;

    section.content_section = nextContentSection;
    delete (section as RawSection & { contentSection?: unknown }).contentSection;
  });

  if (editorState.videoSectionOwnerSectionId) {
    sections.forEach((section, sectionIndex) => {
      const sectionId =
        normalizeIdentifier(section.id) ??
        normalizeIdentifier(section.uid) ??
        `__generated-section-${sectionIndex + 1}`;

      if (sectionId !== editorState.videoSectionOwnerSectionId) {
        return;
      }

      const videoSection = getVideoSection(section);

      if (!videoSection && editorState.videoIds.length === 0) {
        return;
      }

      const nextVideoSection: RawVideoSection = {
        ...(videoSection ?? {}),
        id:
          editorState.videoSectionId ??
          normalizeIdentifier(videoSection?.id) ??
          `${sectionId}-video`,
        uid:
          editorState.videoSectionUid ??
          (typeof videoSection?.uid === 'number' ? videoSection.uid : undefined),
        video_ids: [...editorState.videoIds],
      };

      delete (nextVideoSection as RawVideoSection & { videoIds?: unknown }).videoIds;

      section.video_section = nextVideoSection;
      delete (section as RawSection & { videoSection?: unknown }).videoSection;
    });
  }

  if (editorState.questionSectionOwnerSectionId) {
    sections.forEach((section, sectionIndex) => {
      const sectionId =
        normalizeIdentifier(section.id) ??
        normalizeIdentifier(section.uid) ??
        `__generated-section-${sectionIndex + 1}`;

      if (sectionId !== editorState.questionSectionOwnerSectionId) {
        return;
      }

      const questionSection = getQuestionSection(section, 'homework');
      const questions = finalizedQuestions?.questions ?? questionSection?.questions ?? [];

      if (!questionSection && questions.length === 0) {
        return;
      }

      const nextQuestionSection: RawQuestionSection = {
        ...(questionSection ?? {}),
        id:
          editorState.questionSectionId ??
          normalizeIdentifier(questionSection?.id) ??
          `${sectionId}-questions`,
        questions,
        uid:
          editorState.questionSectionUid ??
          (typeof questionSection?.uid === 'number' ? questionSection.uid : undefined),
      };

      delete (nextQuestionSection as RawQuestionSection & { questionSet?: unknown })
        .questionSet;

      section.question_section = nextQuestionSection;
      delete (section as RawSection & { questionSection?: unknown }).questionSection;
    });
  }

  if (editorState.preLectureQuestionSectionOwnerSectionId) {
    sections.forEach((section, sectionIndex) => {
      const sectionId =
        normalizeIdentifier(section.id) ??
        normalizeIdentifier(section.uid) ??
        `__generated-section-${sectionIndex + 1}`;

      if (sectionId !== editorState.preLectureQuestionSectionOwnerSectionId) {
        return;
      }

      const questionSection = getQuestionSection(section, 'preLecture');
      const questions =
        finalizedQuestions?.preLectureQuestions ?? questionSection?.questions ?? [];

      if (!questionSection && questions.length === 0) {
        return;
      }

      const nextQuestionSection: RawQuestionSection = {
        ...(questionSection ?? {}),
        id:
          editorState.preLectureQuestionSectionId ??
          normalizeIdentifier(questionSection?.id) ??
          `${sectionId}-pre-lecture-questions`,
        questions,
        uid:
          editorState.preLectureQuestionSectionUid ??
          (typeof questionSection?.uid === 'number' ? questionSection.uid : undefined),
      };

      section.pre_lecture_question_section = nextQuestionSection;
      delete (section as RawSection & { preLectureQuestionSection?: unknown })
        .preLectureQuestionSection;
    });
  }

  if (!hasContentSection) {
    throw new Error(
      'Cannot save content because the component does not expose editable content sections.',
    );
  }

  component.sections = sections;

  return {
    components: [component],
    id: courseId,
    sections,
  };
}
